import React, { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDescope, useSession } from '@descope/react-sdk';
import { SidebarHeader, SidebarMenu, SidebarFooter, DocumentHistory } from 'components/organisms';
import { useAlert } from 'context/AlertContext';
import useAuthStore from 'stores/authStore';
import useChatStore from 'stores/chatStore';
import useSidebarStore from 'stores/sidebarStore';
import { analyticsService } from 'services/analyticsService';

interface ChatSidebarProps {
  onNewClassification: () => void;
}

const ChatSidebar: React.FC<ChatSidebarProps> = ({ onNewClassification }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const sdk = useDescope();
  const { isAuthenticated } = useSession();
  const { showSuccess, showError } = useAlert();
  const { getUserId, clearAuth } = useAuthStore();
  const { loadConversation, currentConversationId } = useChatStore();
  const { isCollapsed, toggle } = useSidebarStore();

  // Get user ID from auth store
  const userId = getUserId();

  const handleBackToLanding = () => {
    navigate('/');
  };

  const handleLogout = async () => {
    try {
      // Track logout event BEFORE clearing auth (need userId)
      if (userId) {
        analyticsService.trackLogout(userId);
        console.log('✅ [ChatSidebar] Logout event tracked in Mixpanel');
      } else {
        console.warn('⚠️ [ChatSidebar] No userId available for logout tracking');
      }

      // Call Descope logout
      await sdk.logout();

      // Clear auth store (replaces localStorage clearing)
      clearAuth();

      // Show success message
      showSuccess('Successfully logged out');

      // Navigate to login page
      navigate('/login');

      // Reset Mixpanel session AFTER navigation (with delay to ensure events are sent)
      setTimeout(() => {
        analyticsService.reset();
      }, 500);
    } catch (error) {
      console.error('Logout error:', error);
      showError('Failed to logout. Please try again.');
    }
  };

  const handleDocumentClick = async (documentId: string, conversationId?: string) => {
    console.log('[ChatSidebar] Conversation clicked:', { documentId, conversationId, currentPath: location.pathname });

    // Navigate to chat page if not already there
    if (location.pathname !== '/chat') {
      console.log('[ChatSidebar] Navigating to chat page from:', location.pathname);
      navigate('/chat');
    }

    try {
      // Use conversation_id if available, otherwise fallback to document_id
      const idToLoad = conversationId || documentId;
      console.log('[ChatSidebar] Loading conversation:', idToLoad);
      await loadConversation(idToLoad, userId!);
      showSuccess('Conversation loaded');
    } catch (error: any) {
      console.error('[ChatSidebar] Failed to load conversation:', error);
      showError('Failed to load conversation');
    }
  };

  const handleNewClassification = useCallback(() => {
    if (onNewClassification) {
      onNewClassification();
    }
  }, [onNewClassification]);

  return (
    <div className={`${isCollapsed ? 'w-[72px]' : 'w-64'} bg-white border-r border-gray-200 flex flex-col fixed h-screen z-10 transition-all duration-200`}>
      <SidebarHeader onToggle={toggle} isCollapsed={isCollapsed} />
      <SidebarMenu
        onBackToLanding={handleBackToLanding}
        onNewClassification={handleNewClassification}
        isCollapsed={isCollapsed}
      />

      {/* Document History Section */}
      {!isCollapsed && (
        <div className="flex-1 overflow-y-auto border-t border-gray-100 mt-2">
          {userId && (
            <DocumentHistory
              userId={userId}
              onDocumentClick={handleDocumentClick}
              className="py-2"
              activeConversationId={currentConversationId}
            />
          )}
        </div>
      )}

      {isCollapsed && <div className="flex-1" />}

      <SidebarFooter
        onLogout={handleLogout}
        isAuthenticated={isAuthenticated}
        isCollapsed={isCollapsed}
      />
    </div>
  );
};

export default ChatSidebar;
